import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth, ROLE_SI_ADMIN } from '@/contexts/AuthContext';
import type { EquipamentosListPageProps } from './equipamentosListPage.types';
import AtivosSection from './components/Ativos/AtivosSection';
import EstadiasClienteSection from './components/EstadiasClienteSection';

type AbaEquipamentos = 'ativos' | 'estadias';

/**
 * Tela "Ativos" (aba Ativos do Portal do SI, `/si/ativos`; `/si/equipamentos` redireciona) — inventário próprio
 * (aba Inventário, substituto do GLPI) e Estadias em Cliente. As antigas abas "Custódia" (lista
 * de si_equipamentos por status) e "Inventário" (consulta ao vivo do GLPI) saíram: a aba
 * Inventário cobre as duas — mesmos equipamentos, com ficha/Situação/histórico, e a prévia do
 * "Importar do GLPI" mostra o GLPI inteiro.
 */
export default function EquipamentosListPage({ className, embutida = false }: EquipamentosListPageProps) {
  const { userHasRole } = useAuth();
  const [aba, setAba] = useState<AbaEquipamentos>('ativos');

  // Guarda interna (não só esconder o link) — exclusivo do SI, nem RH (mesmo padrão do
  // drawer de Configurações). Bloqueia mesmo quem digitar a URL direto. Fica depois dos
  // hooks acima (Regras de Hooks: nunca condicionar chamada de hook a um retorno antecipado).
  if (!userHasRole(ROLE_SI_ADMIN)) {
    return (
      <div className={className ?? 'space-y-6 p-6'}>
        <p className="text-sm text-red-600">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  return (
    <div className={className ?? (embutida ? 'space-y-6' : 'space-y-6 p-6')}>
      {!embutida && (
        <div>
          <Link
            to="/si"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden />
            Voltar
          </Link>
          <h1 className="mt-2 font-montserrat text-3xl font-bold text-gray-900">Ativos</h1>
          <p className="mt-1 text-sm text-gray-600">Inventário de ativos e equipamentos em Cliente.</p>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200" data-tour="si-ativos-abas">
        {(
          [
            { valor: 'ativos', label: 'Inventário' },
            { valor: 'estadias', label: 'Estadias em Cliente' },
          ] as const
        ).map((item) => (
          <button
            key={item.valor}
            type="button"
            onClick={() => setAba(item.valor)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              aba === item.valor
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div data-tour="si-ativos-conteudo">
        {aba === 'ativos' && <AtivosSection />}
        {aba === 'estadias' && <EstadiasClienteSection />}
      </div>
    </div>
  );
}
